(() => {
class LfBrandMenu extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.media = matchMedia('(min-width: 1000px)');
    this.input = this.querySelector('input');
    this.results = this.querySelector('[data-brand-results]');
    this.alphabet = this.querySelector('[data-brand-alphabet]');
    this.empty = this.querySelector('[data-brand-empty]');
    this.counter = this.querySelector('[data-brand-count]');
    this.controls = this.querySelector('[data-brand-controls]');
    const options = { signal: this.controller.signal };
    this.input.addEventListener('input', () => this.filter(), options);
    this.media.addEventListener('change', () => this.reset(), options);
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting) && this.isActive()) {
        this.build();
        this.filter();
      }
    });
    this.observer.observe(this);
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.reset();
  }
  isActive() {
    return this.dataset.context === (this.media.matches ? 'desktop' : 'mobile');
  }
  reset() {
    if (this.dataset.context === 'mobile' && this.media.matches) {
      const drawer = this.closest('header-sidebar');
      if (drawer?.hasAttribute('open')) drawer.hide?.();
    }
    this.renderController?.abort();
    this.results.replaceChildren();
    this.alphabet.replaceChildren();
    this.items = null;
    this.groups = [];
    this.input.value = '';
    this.controls.hidden = this.alphabet.hidden = true;
    this.empty.hidden = true;
    this.counter.textContent = '';
    if (this.isActive() && this.getClientRects().length) this.build();
  }
  build() {
    if (this.items || !this.isActive()) return;
    const source = document.getElementById(this.dataset.sourceId);
    if (!(source instanceof HTMLTemplateElement)) return;
    const items = [...source.content.querySelectorAll('[data-lf-brand-name]')];
    if (!items.length) return;
    const collator = new Intl.Collator(document.documentElement.lang || undefined, { sensitivity: 'base', numeric: true });
    items.sort((a, b) => collator.compare(a.dataset.lfBrandName, b.dataset.lfBrandName));
    this.renderController = new AbortController();
    this.items = [];
    this.groups = [];
    this.columns = document.createElement('div');
    this.columns.className = 'lf-brand-menu__columns';
    this.results.append(this.columns);
    const grouped = new Map();
    for (const sourceItem of items) {
      const item = sourceItem.cloneNode(true);
      const link = item.querySelector('a[href]');
      if (!link) continue;
      const url = new URL(link.getAttribute('href'), location.origin);
      if (url.origin !== location.origin || !/^\/collections\//.test(url.pathname)) continue;
      item.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      const normalized = this.normalize(item.dataset.lfBrandName);
      const letter = /^[a-z]/.test(normalized) ? normalized.charAt(0).toUpperCase() : '#';
      if (!grouped.has(letter)) grouped.set(letter, []);
      grouped.get(letter).push(item);
      this.items.push({ item, normalized });
    }
    for (const [letter, itemsForLetter] of grouped) {
      const group = document.createElement('details');
      group.className = 'lf-brand-menu__group';
      group.open = this.media.matches;
      const heading = document.createElement('summary');
      heading.textContent = letter;
      const list = document.createElement('ul');
      list.append(...itemsForLetter);
      group.append(heading, list);
      this.columns.append(group);
      this.groups.push(group);
      const jump = document.createElement('button');
      jump.type = 'button';
      jump.textContent = letter;
      jump.addEventListener('click', () => {
        this.input.value = '';
        this.filter();
        group.open = true;
        heading.focus({ preventScroll: true });
        group.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      }, { signal: this.renderController.signal });
      this.alphabet.append(jump);
    }
    this.controls.hidden = this.alphabet.hidden = false;
    this.filter();
  }
  normalize(value) {
    return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  }
  filter() {
    if (!this.items) return;
    const query = this.normalize(this.input.value);
    let count = 0;
    for (const entry of this.items) {
      const visible = entry.normalized.includes(query);
      entry.item.hidden = !visible;
      if (visible) count++;
    }
    for (const group of this.groups) {
      group.hidden = ![...group.querySelectorAll('li')].some(item => !item.hidden);
      if (query || this.media.matches) group.open = !group.hidden;
    }
    this.empty.hidden = count !== 0;
    this.alphabet.hidden = !!query;
    this.counter.textContent = this.counter.dataset.template.replace('COUNT', String(count));
  }
}
if (!customElements.get('lf-brand-menu')) customElements.define('lf-brand-menu', LfBrandMenu);
class LfBrandsDisclosure extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.details = this.querySelector(':scope > details');
    this.summary = this.details?.querySelector(':scope > summary');
    if (!this.details || !this.summary) return;
    this.desktop = matchMedia('(min-width: 1000px)');
    this.hover = matchMedia('(hover: hover) and (pointer: fine)');
    const options = { signal: this.controller.signal };
    this.summary.setAttribute('aria-expanded', String(this.details.open));
    this.summary.addEventListener('pointerdown', () => { this.pointerActivating = true; }, options);
    this.summary.addEventListener('pointercancel', () => { this.pointerActivating = false; }, options);
    this.summary.addEventListener('click', event => {
      event.preventDefault();
      this.pointerActivating = false;
      this.setOpen(!this.details.open);
    }, options);
    this.addEventListener('pointerenter', () => {
      if (!this.desktop.matches || !this.hover.matches) return;
      clearTimeout(this.closeTimer);
      this.setOpen(true);
    }, options);
    this.addEventListener('pointerleave', () => {
      if (!this.desktop.matches || !this.hover.matches) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.contains(document.activeElement)) this.setOpen(false);
      }, 220);
    }, options);
    this.addEventListener('focusin', event => {
      clearTimeout(this.closeTimer);
      if (event.target === this.summary && !this.details.open && !this.pointerActivating && !this.ignoreFocus) {
        this.setOpen(true);
      }
    }, options);
    this.addEventListener('focusout', event => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        if (!this.contains(document.activeElement)) this.setOpen(false);
      }, 0);
    }, options);
    this.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !this.details.open) return;
      event.preventDefault();
      event.stopPropagation();
      this.ignoreFocus = true;
      this.summary.focus();
      this.setOpen(false);
      this.ignoreFocus = false;
    }, options);
    document.addEventListener('pointerdown', event => {
      if (this.details.open && !this.contains(event.target)) this.setOpen(false);
    }, options);
    this.desktop.addEventListener('change', () => this.setOpen(false), options);
    this.details.addEventListener('toggle', () => {
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      if (this.details.open) this.querySelector('lf-brand-menu')?.build();
    }, options);
  }
  disconnectedCallback() {
    clearTimeout(this.closeTimer);
    clearTimeout(this.focusTimer);
    this.controller?.abort();
    this.controller = null;
  }
  setOpen(open) {
    clearTimeout(this.closeTimer);
    if (open && !this.desktop.matches) return;
    if (open && !this.details.open) {
      this.closest('.header__primary-nav')?.querySelectorAll('dropdown-menu-disclosure, mega-menu-disclosure').forEach(menu => menu.toggle?.(false));
    }
    this.details.open = open;
    this.summary.setAttribute('aria-expanded', String(open));
    if (open) this.querySelector('lf-brand-menu')?.build();
  }
}
if (!customElements.get('lf-brands-disclosure')) customElements.define('lf-brands-disclosure', LfBrandsDisclosure);

})();
